import React, { useState } from "react";
import { Alert, Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { AcceptanceProofFields, useAcceptanceProof } from "./AcceptanceProof";
import { acceptTripPolicy, reportPolicyLocationFailure } from "../services/tripTrackingApi";
import { formatDateTimeDMY12 } from "../utils/dateFormat";

// The trip policy a customer accepts on the Trip Details link before the driver, vehicle, hotels
// and trip plan open. Customers kept saying the policies were never discussed; this records who
// accepted, where they were, and the exact points they read. The server leaves the locked details
// out of the response until then, so this page only has to explain and collect the acceptance.

const LANG_KEY = "tripPolicyLang";

const ALL_POLICY_LANGS = [
  ["en", "English"],
  ["te", "తెలుగు"],
  ["kn", "ಕನ್ನಡ"],
  ["ta", "தமிழ்"],
  ["hi", "हिंदी"],
];

// English only for now. The other languages' page text below is ready; add their codes here (and
// in the admin editor's LANGS) once the translations have been checked.
const ENABLED_LANG_CODES = ["en"];

export const POLICY_LANGS = ALL_POLICY_LANGS.filter(([code]) => ENABLED_LANG_CODES.includes(code));

const TEXT = {
  en: {
    language: "Language",
    title: "Important Policy Points",
    intro: "Please read these points carefully. Your vehicle, hotel and trip plan details will open after you accept.",
    lockedText: "Accept the trip policy below to view",
    driverVehicle: "Driver & Vehicle Details",
    stays: "Day Wise Stay",
    tripPlan: "Trip Plan",
    pickupDrop: "Vehicle Pickup & Drop",
    pickup: "Vehicle Pickup Date & Time:",
    drop: "Vehicle Drop Date & Time:",
    checkbox: "I have read and accept these policies",
    accept: "Accept and view trip details",
    saving: "Saving...",
    needs: "Tick the box, enter your name and allow location to continue.",
    acceptedBy: (name, date) => `Trip policy accepted by ${name} on ${date}`,
    fields: {
      name: "Your name",
      namePlaceholder: "Enter your full name",
      location: "Your location",
      locating: "Getting your location...",
      located: "Location captured",
      proofNote: "Your name, location and device details will be saved as proof that you accepted this policy.",
    },
  },
  te: {
    language: "భాష",
    title: "ముఖ్యమైన పాలసీ నియమాలు",
    intro: "దయచేసి ఈ నియమాలను జాగ్రత్తగా చదవండి. మీరు అంగీకరించిన తర్వాత మీ వాహనం, హోటల్ మరియు ట్రిప్ ప్లాన్ వివరాలు కనిపిస్తాయి.",
    lockedText: "చూడటానికి కింద ఉన్న ట్రిప్ పాలసీని అంగీకరించండి",
    driverVehicle: "డ్రైవర్ & వాహనం వివరాలు",
    stays: "రోజువారీ బస",
    tripPlan: "ట్రిప్ ప్లాన్",
    pickupDrop: "వాహనం పికప్ & డ్రాప్",
    pickup: "వాహనం పికప్ తేదీ & సమయం:",
    drop: "వాహనం డ్రాప్ తేదీ & సమయం:",
    checkbox: "నేను ఈ పాలసీలను చదివి అంగీకరిస్తున్నాను",
    accept: "అంగీకరించి ట్రిప్ వివరాలు చూడండి",
    saving: "సేవ్ చేస్తోంది...",
    needs: "కొనసాగడానికి బాక్స్‌ను టిక్ చేసి, మీ పేరు నమోదు చేసి, Location కు అనుమతి ఇవ్వండి.",
    acceptedBy: (name, date) => `${name} గారు ${date} న ట్రిప్ పాలసీని అంగీకరించారు`,
    fields: {
      name: "మీ పేరు",
      namePlaceholder: "మీ పూర్తి పేరు నమోదు చేయండి",
      location: "మీ లొకేషన్",
      locating: "మీ లొకేషన్ తీసుకుంటోంది...",
      located: "లొకేషన్ నమోదైంది",
      proofNote: "మీరు ఈ పాలసీని అంగీకరించినట్లు రుజువుగా మీ పేరు, లొకేషన్ మరియు ఫోన్ వివరాలు సేవ్ చేయబడతాయి.",
    },
  },
  kn: {
    language: "ಭಾಷೆ",
    title: "ಪ್ರಮುಖ ನೀತಿ ಅಂಶಗಳು",
    intro: "ದಯವಿಟ್ಟು ಈ ಅಂಶಗಳನ್ನು ಗಮನವಿಟ್ಟು ಓದಿ. ನೀವು ಒಪ್ಪಿದ ನಂತರ ನಿಮ್ಮ ವಾಹನ, ಹೋಟೆಲ್ ಮತ್ತು ಪ್ರವಾಸ ಯೋಜನೆಯ ವಿವರಗಳು ತೆರೆಯುತ್ತವೆ.",
    lockedText: "ನೋಡಲು ಕೆಳಗಿನ ಪ್ರವಾಸ ನೀತಿಯನ್ನು ಒಪ್ಪಿಕೊಳ್ಳಿ",
    driverVehicle: "ಚಾಲಕ ಮತ್ತು ವಾಹನದ ವಿವರಗಳು",
    stays: "ದಿನವಾರು ವಾಸ್ತವ್ಯ",
    tripPlan: "ಪ್ರವಾಸ ಯೋಜನೆ",
    pickupDrop: "ವಾಹನ ಪಿಕಪ್ ಮತ್ತು ಡ್ರಾಪ್",
    pickup: "ವಾಹನ ಪಿಕಪ್ ದಿನಾಂಕ ಮತ್ತು ಸಮಯ:",
    drop: "ವಾಹನ ಡ್ರಾಪ್ ದಿನಾಂಕ ಮತ್ತು ಸಮಯ:",
    checkbox: "ನಾನು ಈ ನೀತಿಗಳನ್ನು ಓದಿದ್ದೇನೆ ಮತ್ತು ಒಪ್ಪುತ್ತೇನೆ",
    accept: "ಒಪ್ಪಿ ಪ್ರವಾಸದ ವಿವರಗಳನ್ನು ನೋಡಿ",
    saving: "ಉಳಿಸಲಾಗುತ್ತಿದೆ...",
    needs: "ಮುಂದುವರಿಯಲು ಬಾಕ್ಸ್ ಟಿಕ್ ಮಾಡಿ, ನಿಮ್ಮ ಹೆಸರು ನಮೂದಿಸಿ ಮತ್ತು Location ಗೆ ಅನುಮತಿ ನೀಡಿ.",
    acceptedBy: (name, date) => `${name} ಅವರು ${date} ರಂದು ಪ್ರವಾಸ ನೀತಿಯನ್ನು ಒಪ್ಪಿಕೊಂಡಿದ್ದಾರೆ`,
    fields: {
      name: "ನಿಮ್ಮ ಹೆಸರು",
      namePlaceholder: "ನಿಮ್ಮ ಪೂರ್ಣ ಹೆಸರು ನಮೂದಿಸಿ",
      location: "ನಿಮ್ಮ ಸ್ಥಳ",
      locating: "ನಿಮ್ಮ ಸ್ಥಳ ಪಡೆಯಲಾಗುತ್ತಿದೆ...",
      located: "ಸ್ಥಳ ದಾಖಲಾಗಿದೆ",
      proofNote: "ನೀವು ಈ ನೀತಿಯನ್ನು ಒಪ್ಪಿದ್ದಕ್ಕೆ ಪುರಾವೆಯಾಗಿ ನಿಮ್ಮ ಹೆಸರು, ಸ್ಥಳ ಮತ್ತು ಫೋನ್ ವಿವರಗಳನ್ನು ಉಳಿಸಲಾಗುತ್ತದೆ.",
    },
  },
  ta: {
    language: "மொழி",
    title: "முக்கியமான கொள்கை விதிகள்",
    intro: "இந்த விதிகளை கவனமாகப் படிக்கவும். நீங்கள் ஏற்றுக்கொண்ட பிறகு உங்கள் வாகனம், ஹோட்டல் மற்றும் பயணத் திட்ட விவரங்கள் திறக்கும்.",
    lockedText: "பார்க்க கீழே உள்ள பயணக் கொள்கையை ஏற்றுக்கொள்ளவும்",
    driverVehicle: "ஓட்டுநர் & வாகன விவரங்கள்",
    stays: "நாள்வாரி தங்குமிடம்",
    tripPlan: "பயணத் திட்டம்",
    pickupDrop: "வாகன பிக்கப் & டிராப்",
    pickup: "வாகன பிக்கப் தேதி & நேரம்:",
    drop: "வாகன டிராப் தேதி & நேரம்:",
    checkbox: "இந்த கொள்கைகளைப் படித்து ஏற்றுக்கொள்கிறேன்",
    accept: "ஏற்றுக்கொண்டு பயண விவரங்களைப் பார்க்கவும்",
    saving: "சேமிக்கப்படுகிறது...",
    needs: "தொடர பெட்டியை டிக் செய்து, உங்கள் பெயரை உள்ளிட்டு, Location-க்கு அனுமதி கொடுக்கவும்.",
    acceptedBy: (name, date) => `${name} அவர்கள் ${date} அன்று பயணக் கொள்கையை ஏற்றுக்கொண்டார்`,
    fields: {
      name: "உங்கள் பெயர்",
      namePlaceholder: "உங்கள் முழுப் பெயரை உள்ளிடவும்",
      location: "உங்கள் இருப்பிடம்",
      locating: "உங்கள் இருப்பிடம் பெறப்படுகிறது...",
      located: "இருப்பிடம் பதிவானது",
      proofNote: "நீங்கள் இந்தக் கொள்கையை ஏற்றுக்கொண்டதற்கான சான்றாக உங்கள் பெயர், இருப்பிடம் மற்றும் போன் விவரங்கள் சேமிக்கப்படும்.",
    },
  },
  hi: {
    language: "भाषा",
    title: "महत्वपूर्ण नीति बिंदु",
    intro: "कृपया इन बिंदुओं को ध्यान से पढ़ें। स्वीकार करने के बाद आपके वाहन, होटल और ट्रिप प्लान की जानकारी खुल जाएगी।",
    lockedText: "देखने के लिए नीचे दी गई ट्रिप पॉलिसी स्वीकार करें",
    driverVehicle: "ड्राइवर और वाहन की जानकारी",
    stays: "दिनवार ठहराव",
    tripPlan: "ट्रिप प्लान",
    pickupDrop: "वाहन पिकअप और ड्रॉप",
    pickup: "वाहन पिकअप तारीख और समय:",
    drop: "वाहन ड्रॉप तारीख और समय:",
    checkbox: "मैंने ये नीतियाँ पढ़ ली हैं और मैं इन्हें स्वीकार करता/करती हूँ",
    accept: "स्वीकार करें और ट्रिप की जानकारी देखें",
    saving: "सेव हो रहा है...",
    needs: "आगे बढ़ने के लिए बॉक्स पर टिक करें, अपना नाम लिखें और Location की अनुमति दें।",
    acceptedBy: (name, date) => `${name} ने ${date} को ट्रिप पॉलिसी स्वीकार की`,
    fields: {
      name: "आपका नाम",
      namePlaceholder: "अपना पूरा नाम लिखें",
      location: "आपकी लोकेशन",
      locating: "आपकी लोकेशन ली जा रही है...",
      located: "लोकेशन दर्ज हो गई",
      proofNote: "आपने यह पॉलिसी स्वीकार की, इसके प्रमाण के रूप में आपका नाम, लोकेशन और फ़ोन की जानकारी सेव की जाएगी।",
    },
  },
};

const rememberedLang = () => {
  try {
    const saved = localStorage.getItem(LANG_KEY);
    return TEXT[saved] && ENABLED_LANG_CODES.includes(saved) ? saved : "en";
  } catch {
    return "en";
  }
};

/** The reader's language for this page, remembered on their phone. */
export function useTripPolicyLang() {
  const [lang, setLang] = useState(rememberedLang);
  const chooseLang = (code) => {
    setLang(code);
    try {
      localStorage.setItem(LANG_KEY, code);
    } catch {
      // Private windows can refuse storage; English again next visit.
    }
  };
  return [lang, chooseLang];
}

/** A point in the chosen language, or English where that language was left blank. */
function pointText(point, lang) {
  const text = String(point?.[lang] || "").trim();
  return text || String(point?.en || "").trim();
}

function LanguageBar({ lang, onChange }) {
  if (POLICY_LANGS.length < 2) return null;
  return (
    <div className="trip-policy-langs">
      <span className="small fw-semibold me-1">{TEXT[lang].language}:</span>
      {POLICY_LANGS.map(([code, label]) => (
        <Button
          key={code}
          size="sm"
          variant={code === lang ? "dark" : "outline-dark"}
          className="py-0 px-2"
          onClick={() => onChange(code)}
        >
          {label}
        </Button>
      ))}
    </div>
  );
}

function LockedCard({ title, text }) {
  return (
    <Card body className="trip-policy-locked-card">
      <i className="bi bi-lock-fill trip-policy-lock-icon" aria-hidden="true" />
      <div>
        <div className="fw-semibold">{title}</div>
        <div className="small">{text}</div>
      </div>
    </Card>
  );
}

/** "Accepted by" line shown above the unlocked page. */
export function TripPolicyAcceptedNote({ policy }) {
  const [lang] = useTripPolicyLang();
  if (!policy?.accepted) return null;
  const text = TEXT[lang] || TEXT.en;
  return (
    <Alert variant="success" className="py-2 mb-0 small">
      <i className="bi bi-check-circle-fill me-1" aria-hidden="true" />
      {text.acceptedBy(policy.acceptedByName || "-", formatDateTimeDMY12(policy.acceptedAt, "-"))}
    </Alert>
  );
}

/**
 * Everything the customer sees while the trip is locked: language choice, the pickup/drop times
 * they already have on WhatsApp, the locked sections, and the points to accept.
 */
export function TripPolicyLockedView({ tracking, token, onAccepted }) {
  const [lang, setLang] = useTripPolicyLang();
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);
  // Location is asked for once the box is ticked, after the points have been read. Failures go to
  // Manage Bookings so the office can see which browser refused and why.
  const proofState = useAcceptanceProof(agreed, (failure) => reportPolicyLocationFailure(token, failure));
  const text = TEXT[lang] || TEXT.en;
  const policy = tracking.policy || {};
  const points = (policy.points || []).map((point) => pointText(point, lang)).filter(Boolean);
  const canAccept = agreed && Boolean(proofState.proof) && !saving;

  const accept = async () => {
    if (!canAccept) return;
    setSaving(true);
    try {
      const data = await acceptTripPolicy(token, { versionId: policy.versionId, language: lang, ...proofState.proof });
      proofState.markUsed();
      onAccepted(data);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not save your acceptance. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="vstack gap-3">
      <LanguageBar lang={lang} onChange={setLang} />

      {tracking.vehiclePickupDateTime || tracking.vehicleDropDateTime ? (
        <Card body className="public-tracking-info-card" data-tour="policy-pickup">
          <div className="public-tracking-info-icon">PD</div>
          <div className="fw-semibold mb-2">{text.pickupDrop}</div>
          <Row className="g-2 small">
            <Col md={6}>
              <span className="text-muted">{text.pickup}</span>{" "}
              {formatDateTimeDMY12(tracking.vehiclePickupDateTime, "-")}
            </Col>
            <Col md={6}>
              <span className="text-muted">{text.drop}</span>{" "}
              {formatDateTimeDMY12(tracking.vehicleDropDateTime, "-")}
            </Col>
          </Row>
        </Card>
      ) : null}

      <div className="vstack gap-3" data-tour="policy-locked">
        <LockedCard title={text.driverVehicle} text={text.lockedText} />
        <LockedCard title={text.stays} text={text.lockedText} />
        <LockedCard title={text.tripPlan} text={text.lockedText} />
      </div>

      <Card className="trip-policy-card" data-tour="policy-accept">
        <Card.Body>
          <h2 className="h5 fw-bold mb-1">{text.title}</h2>
          <p className="small mb-3">{text.intro}</p>
          <ol className="trip-policy-points">
            {points.map((point, index) => (
              <li key={index}>{point}</li>
            ))}
          </ol>

          <Form.Check
            id="trip-policy-agree"
            className="trip-policy-agree mb-3"
            checked={agreed}
            disabled={saving}
            onChange={(event) => setAgreed(event.target.checked)}
            label={text.checkbox}
          />

          {agreed ? (
            <AcceptanceProofFields state={proofState} disabled={saving} labels={text.fields} lang={lang} />
          ) : null}

          <Button variant="success" className="w-100 mt-3" disabled={!canAccept} onClick={accept}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {text.saving}
              </>
            ) : (
              text.accept
            )}
          </Button>
          {!canAccept && !saving ? <div className="small text-center mt-2 trip-policy-hint">{text.needs}</div> : null}
        </Card.Body>
      </Card>
    </div>
  );
}
